import { action, computed, makeObservable, observable } from 'mobx';
import { RouteV2Id } from 'routes/routesConfigV2';
import GroupModule from 'store/modules/group/groupModule';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';
import { redirectToRoute } from 'routes/redirectToRoute';
import { SidebarDelegate } from './sidebarDelegate';
import { EnrollmentGroupInfoDelegate } from './enrollmentGroupInfoDelegate';

export interface EnrollmentFlowDomainParams {
  groupId: string;
  periodId: string;
}

export interface EnrollmentFlowPageConstructorParams {
  domainParams: EnrollmentFlowDomainParams;
  nextPageId: RouteV2Id;
}

export default abstract class EnrollmentFlowPage {
  protected _domainData: EnrollmentFlowDomainParams;
  protected _nextPageId: RouteV2Id;
  protected _groupModule: GroupModule;
  protected _enrollmentPeriodModule: EnrollmentPeriodModule;
  protected _isInitialDataLoading = false;
  public sidebarDelegate: SidebarDelegate;
  public groupInfoDelegate: EnrollmentGroupInfoDelegate;

  public get domainData(): EnrollmentFlowDomainParams {
    return this._domainData;
  }

  /** Used for the separate layout components: sidebar, group info blob, etc. */
  public get isFlowDataLoading(): boolean {
    return this._groupModule.isLoading || this._enrollmentPeriodModule.isLoading;
  }

  /** Used for the initial page content loader */
  public get isInitialDataLoading(): boolean {
    return this._isInitialDataLoading || this.isFlowDataLoading;
  }

  public setIsInitialDataLoading(isInitialDataLoading: boolean): void {
    this._isInitialDataLoading = isInitialDataLoading;
  }

  public async getFlowInitialData(): Promise<void> {
    await Promise.all([
      this._groupModule.getGroup(this._domainData.groupId),
      this._enrollmentPeriodModule.uploadEnrollmentPeriod(this._domainData.periodId),
    ]);
  }

  public onCompleteRedirect(): void {
    redirectToRoute(this._nextPageId, { ...this._domainData });
  }

  constructor({ domainParams, nextPageId }: EnrollmentFlowPageConstructorParams) {
    this._domainData = domainParams;
    this._nextPageId = nextPageId;
    this._groupModule = new GroupModule();
    this._enrollmentPeriodModule = new EnrollmentPeriodModule();
    this.sidebarDelegate = new SidebarDelegate(this._groupModule, this._enrollmentPeriodModule);
    this.groupInfoDelegate = new EnrollmentGroupInfoDelegate(this._groupModule, this._enrollmentPeriodModule);

    makeObservable<EnrollmentFlowPage, '_domainData' | '_nextPageId' | '_groupModule' | '_isInitialDataLoading'>(this, {
      _domainData: observable,
      _nextPageId: observable,
      _groupModule: observable,
      _isInitialDataLoading: observable,
      domainData: computed,
      isInitialDataLoading: computed,
      setIsInitialDataLoading: action.bound,
      getFlowInitialData: action.bound,
      isFlowDataLoading: computed,
      onCompleteRedirect: action.bound,
    });
  }
}
